import { createContext, useContext, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Chip from "../../../ui/feedback/Chip";
import { classNames } from "../../../ui/styles/classNames";
import { orderedLabels } from "../../ontology";
import { termName, type TermCards } from "../../ontology/terms/termNames";
import { classesAbove, mostSpecificClasses, nestSaid, type Individual, type NestedDescription, type ParticipationShape, type RoleWordShape, type StatementCardShape, type StatementLayout, type StatementValue } from "./aboxViewModel";
import { DetailLevelContext } from "./detailLevel";
import { statementCard, type StatementCard } from "./statementCard";
import { readAmount, readDate, type AmountReadingShape, type DateReadingShape } from "./valueReadings";
import TermLink from "./TermLink";

interface StatementSummaryProps {
  /** The statement as its view lays it out (`layOutStatement`). */
  readonly layout: StatementLayout;
  /** What the agent wrote about it; `""` for nothing. */
  readonly note: string;
  /** The commit of the data model version it was written against, where its terms open. */
  readonly modelCommit: string;
  /** The terms of that version in the statement's own project, which name them; `null` names them by their IRIs' ends. */
  readonly cards: TermCards | null;
  /** Its number in reading order, which its words in the text also carry; `null` for none. */
  readonly number: number | null;
  /** How the project's profile reads one statement (Daniel's card); `null` shows it as written. */
  readonly shape: StatementCardShape | null;
  /** How its dates and amounts read as one value (the profile's `dates` and `amounts`); without, as written. */
  readonly readings?: ValueReadings;
  /** The other statements shown beside it, by what each is about; without, a value names only itself. */
  readonly references?: StatementReferences;
}

/**
 * The statements shown together, by the IRI of what each is about (its card's event or state, `<iri>` as `Individual`
 * writes it): a value naming one reads as that statement's "#n kind" and chooses it, as Daniel's viewer refers to a
 * statement by its number.
 */
export interface StatementReferences {
  readonly labelOf: ReadonlyMap<string, string>;
  readonly onChoose: (about: string) => void;
}

const NO_REFERENCES: StatementReferences = { labelOf: new Map(), onChoose: () => undefined };
const ReferencesContext = createContext<StatementReferences>(NO_REFERENCES);

/** The profile's readings of dates and amounts, for every value of one statement. */
export interface ValueReadings {
  readonly dates: DateReadingShape | null;
  readonly amounts: AmountReadingShape | null;
}

const NO_READINGS: ValueReadings = { dates: null, amounts: null };
const ReadingsContext = createContext<ValueReadings>(NO_READINGS);

/**
 * One statement as a historian reads it (F94, O-1): the class of what it is about and its name,
 * then one row per property under the data model's label. Its words are the text's, shown there,
 * not quoted again; a term opens on the Data model beside (F75). The evidence that anchors it in
 * the text, such as its mentions and attestations, follows closed (AB-5), with the note when the
 * statement itself only names things.
 */
export default function StatementSummary({ readings = NO_READINGS, references = NO_REFERENCES, ...props }: StatementSummaryProps) {
  return (
    <ReadingsContext.Provider value={readings}>
      <ReferencesContext.Provider value={references}><Summary {...props} /></ReferencesContext.Provider>
    </ReadingsContext.Provider>
  );
}

function Summary({ layout, note, modelCommit, cards, number, shape }: Omit<StatementSummaryProps, "readings" | "references">) {
  const { t } = useTranslation();
  // What it says, as one whole: each part under the value that names it (`nestSaid`).
  const [main, ...others] = nestSaid(layout.said);
  const card = statementCard(main, shape);
  const numbered = number === null ? null : <b className="app-title tabular-nums">#{number}</b>;
  const separated = (description: NestedDescription) => (
    <div key={description.individual.iri} className="border-t border-[var(--app-border)] pt-1">
      <Described description={description} cards={cards} commit={modelCommit} />
    </div>
  );
  // Daniel's levels: the essentials show what it says only; details add the evidence, folded, with the agent's note in
  // it; technical opens the fold. The note is the agent's reasoning, not what the data says, so it never stands open
  // beside the claim (usability round 4: "Move it under Evidence (Details) or Technical"); a statement without evidence
  // shows its note at the technical level only.
  const level = useContext(DetailLevelContext);
  const noteText = note === "" || level === "essentials" ? null : <p className="app-text-muted text-xs">{note}</p>;
  const evidence = <>{layout.evidence.map(separated)}{noteText}</>;
  return (
    <div className="space-y-1">
      {card !== null ? <Card card={card} number={numbered} cards={cards} commit={modelCommit} participation={shape?.participation ?? null} />
        : main === undefined ? null : <NestedValue description={main} cards={cards} commit={modelCommit} number={numbered} top />}
      {others.map(separated)}
      {layout.evidence.length === 0 ? (level === "technical" ? noteText : null) : (level === "essentials" && main !== undefined) ? null : main === undefined ? (
        // A line that is only evidence sits in its part's one evidence fold (PartStatements): shown as it is, no label.
        <div className="space-y-1 text-xs">{evidence}</div>
      ) : (
        <details className="space-y-1 text-xs" open={level === "technical"}>
          <summary className="app-text-muted w-fit cursor-pointer">{t("data.abox.evidence")}</summary>
          {evidence}
        </details>
      )}
    </div>
  );
}

interface Named {
  readonly cards: TermCards | null;
  readonly commit: string;
}

/**
 * What the text calls it: its label, else the words that say it (Daniel's surface form). Those
 * words are often the whole passage beside it, so they take one line, the rest on hover.
 */
function NameOf({ individual, className }: { individual: Individual; className?: string }) {
  if (individual.name !== null) return <span className={className}>{individual.name}</span>;
  if (individual.words === null) return null;
  return <span className={classNames("inline-block max-w-full truncate align-bottom", className)} title={individual.words}>{individual.words}</span>;
}

/** Its most specific classes, by the data model's parents; all of them until those load. */
function kindsOf(individual: Individual, cards: TermCards | null): string[] {
  return mostSpecificClasses(individual.classes, (iri) => cards?.get(iri)?.parents ?? []);
}

/**
 * Daniel's statement card: its number and kind, the class of what it is about; who takes part in
 * which role, one line each; then the rest it says under the data model's labels.
 */
function Card({ card, number, participation, cards, commit }: Named & { card: StatementCard; number: ReactNode; participation: ParticipationShape | null }) {
  const { t } = useTranslation();
  // Who takes part reads as "X as petitioner", under no property label: "has participation" opened nearly every card
  // (usability round 5). The technical level names the property, as it shows the structure.
  const technical = useContext(DetailLevelContext) === "technical";
  return (
    <>
      <p className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
        {number}
        {kindsOf(card.about.individual, cards).map((iri) => <Chip key={iri}><TermLink iri={iri} commit={commit} cards={cards} /></Chip>)}
        {card.about.individual.name === null ? null : <NameOf individual={card.about.individual} className="app-title font-semibold" />}
      </p>
      <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-0.5">
        {participation === null || card.participants.length === 0 ? null : (
          <div className="contents">
            {technical ? <dt className="app-text-muted"><TermLink iri={participation.property} commit={commit} cards={cards} /></dt> : null}
            <dd className={technical ? "min-w-0" : "col-span-2 min-w-0"}>
              {card.participants.map((participant, index) => (
                <div key={index}>
                  {participant.actor === null ? "?" : <Value value={participant.actor} cards={cards} commit={commit} />}
                  {participant.role === null ? null : <Role role={participant.role} cards={cards} commit={commit} asRole={t("data.abox.asRole")} event={card.about.individual.classes} roleWords={participation.roleWords ?? []} />}
                </div>
              ))}
            </dd>
          </div>
        )}
        <Rows rows={card.rows} cards={cards} commit={commit} />
      </dl>
    </>
  );
}

/**
 * A participant's role. A role from the data model, such as petitioner, reads "as petitioner"; one the data itself
 * describes, such as a benefice, is named by its class instead, "· benefice: …": "X as <benefice>" misreads as X
 * being the church (Daniel's viewer, `participant`). Where the profile words such a role for the statement's event,
 * it reads "as previous holder of …" (interim `roleWords`, until the data model labels its role terms).
 */
function Role({ role, cards, commit, asRole, event, roleWords }: Named & { role: NonNullable<StatementCard["participants"][number]["role"]>; asRole: string; event: readonly string[]; roleWords: readonly RoleWordShape[] }) {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "en";
  const individual = role.kind === "individual" ? role.individual : role.kind === "nested" ? role.description.individual : null;
  if (individual === null) return <><span className="app-text-muted"> {asRole} </span><Value value={role} cards={cards} commit={commit} /></>;
  const withParents = (classes: readonly string[]) => new Set([...classes, ...classesAbove(classes, (iri) => cards?.get(iri)?.parents ?? [])]);
  const events = withParents(event);
  const roles = withParents(individual.classes);
  const worded = roleWords.find((each) => events.has(each.event) && roles.has(each.roleClass));
  const words = worded === undefined ? undefined : orderedLabels(worded.words, language)[0]?.value;
  if (words !== undefined) return <><span className="app-text-muted"> {asRole} {words} </span><NameOf individual={individual} /></>;
  const kind = kindsOf(individual, cards).map((iri) => termName(iri, cards, language)).join(", ");
  return (
    <>
      <span className="app-text-muted"> · {kind === "" ? asRole : `${kind}:`} </span>
      <NameOf individual={individual} />
    </>
  );
}

function Described({ description, cards, commit, number = null }: Named & { description: NestedDescription; number?: ReactNode }) {
  const { individual, rows } = description;
  // Something described by its properties is named only by a label: its words are the text beside it.
  const named = rows.length === 0 || individual.name !== null;
  return (
    <>
      <p className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
        {number}
        {kindsOf(individual, cards).map((iri) => <Chip key={iri}><TermLink iri={iri} commit={commit} cards={cards} /></Chip>)}
        {named ? <NameOf individual={individual} className="app-title font-semibold" /> : null}
      </p>
      {rows.length === 0 ? null : (
        <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-0.5">
          <Rows rows={rows} cards={cards} commit={commit} />
        </dl>
      )}
    </>
  );
}

function Rows({ rows, cards, commit }: Named & { rows: NestedDescription["rows"] }) {
  const references = useContext(ReferencesContext);
  // A part described further stands under its value; one that only names another statement's subject reads as it.
  const described = (value: NestedDescription["rows"][number]["values"][number]) => value.kind === "nested"
    && (value.description.rows.length > 0 || !references.labelOf.has(value.description.individual.iri));
  return rows.map((row) => (
    <div key={row.property} className="contents">
      <dt className="app-text-muted"><TermLink iri={row.property} commit={commit} cards={cards} /></dt>
      <dd className="min-w-0">
        {row.values.map((value, index) => (value.kind === "nested" && described(value) ? (
          <NestedValue key={index} description={value.description} cards={cards} commit={commit} />
        ) : <span key={index}>{index === 0 ? null : ", "}<Value value={value} cards={cards} commit={commit} /></span>))}
      </dd>
    </div>
  ));
}

/**
 * A part this statement describes, under the value that names it. A date or an amount reads as one value by the
 * profile ("Aug 26, 1455 (Julian calendar)", "8 mark of silver"), with the source's words on hover and any row the
 * reading doesn't use beneath it; anything else as written.
 */
function NestedValue({ description, cards, commit, number = null, top = false }: Named & { description: NestedDescription; number?: ReactNode; top?: boolean }) {
  const { i18n } = useTranslation();
  const readings = useContext(ReadingsContext);
  const classes = [...description.individual.classes, ...classesAbove(description.individual.classes, (iri) => cards?.get(iri)?.parents ?? [])];
  const isOf = (shape: readonly string[]) => classes.some((iri) => shape.includes(iri));
  const date = readDate(description, readings.dates, isOf, i18n.resolvedLanguage ?? "en");
  const amount = date === null ? readAmount(description, readings.amounts, isOf) : null;
  // At the technical level a read value also shows all its own rows, the structure under the reading (Sol R10).
  const level = useContext(DetailLevelContext);
  const read = date ?? amount;
  const rest = read === null ? null : level === "technical" ? description.rows : read.rest;
  if (rest === null) {
    // What a statement itself is about stands at its top; a part it describes sits under the value that names it.
    return top ? <Described description={description} cards={cards} commit={commit} number={number} /> : (
      <div className="border-l-2 border-[var(--app-border)] pl-2">
        <Described description={description} cards={cards} commit={commit} />
      </div>
    );
  }
  const reading = (
    <>
      {date === null ? null : (
        <span title={date.original ?? undefined}>
          {date.text}
          {date.calendar === null ? null : <span className="app-text-muted"> (<Value value={date.calendar} cards={cards} commit={commit} />)</span>}
        </span>
      )}
      {amount === null ? null : <span>{amount.value} <Value value={amount.unit} cards={cards} commit={commit} /></span>}
    </>
  );
  return (
    <div>
      {/* A statement that is itself a date or an amount reads as one too, under its number and kind. */}
      {top ? (
        <p className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
          {number}
          {kindsOf(description.individual, cards).map((iri) => <Chip key={iri}><TermLink iri={iri} commit={commit} cards={cards} /></Chip>)}
          {reading}
        </p>
      ) : reading}
      {rest.length === 0 ? null : (
        <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-xs">
          <Rows rows={rest} cards={cards} commit={commit} />
        </dl>
      )}
    </div>
  );
}

function Value({ value, cards, commit }: Named & { value: StatementValue | { readonly kind: "nested"; readonly description: NestedDescription } }) {
  const { t, i18n } = useTranslation();
  const references = useContext(ReferencesContext);
  switch (value.kind) {
    case "text":
      return <>{value.text}</>;
    case "term":
      return <TermLink iri={value.iri} commit={commit} cards={cards} />;
    case "individual":
    case "nested": {
      // A participant described in the same statement is named in its line, like any other.
      const individual = value.kind === "nested" ? value.description.individual : value.individual;
      // What another statement shown here is about reads as that statement, "#4 Vacancy", not as the words it was
      // drawn from (usability round 5: "acknowledges" showed clipped fragments and didn't name the statement).
      const reference = references.labelOf.get(individual.iri);
      if (reference !== undefined) {
        return <button type="button" className="app-link font-medium" onClick={() => { references.onChoose(individual.iri); }}>{reference}</button>;
      }
      const kind = kindsOf(individual, cards).map((iri) => termName(iri, cards, i18n.resolvedLanguage ?? "en")).join(", ");
      if (individual.name === null && individual.words === null) return <>{kind === "" ? t("sources.document.regest.unnamed") : kind}</>;
      return <><NameOf individual={individual} />{kind === "" ? null : <span className="app-text-muted text-xs"> {kind}</span>}</>;
    }
  }
}
